import { API } from '@thuas/pd-schemas';
import api from 'helpers/api';
import { callGet, Retrieving } from 'helpers/apiTypes';
import { prepareForBackend, toArrayOfIdObject } from 'helpers/objects';

export type IProject = API['Project']['read'];
export type IProjectCreate = API['Project']['create'];
export type IProjectUpdate = API['Project']['update'] & {
  publicationsOverwrite?: { id: number }[];
};

function prepareForUpdate(project: IProject): IProjectUpdate {
  const updated = prepareForBackend(project, ['id']) as IProjectUpdate;
  updated.publicationsOverwrite = toArrayOfIdObject(project.publications);
  return updated;
}
function prepareForCreate(project: IProject): IProjectCreate {
  return prepareForBackend(
    {
      ...project,
    },
    ['id'],
  ) as IProjectCreate;
}

type fetchProjectsOptions = {
  all?: boolean;
  concept?: boolean;
  active?: boolean;
  finished?: boolean;
  archived?: boolean;
};
export const fetchProjects = async (options?: fetchProjectsOptions) => {
  try {
    const { all, concept, active, finished, archived } = options ?? {};
    const query = {
      '*': true,
      image: {
        '*': true,
      },
      publications: {
        '*': true,
      },
      author: {
        id: true,
        username: true,
      },
    } as const satisfies Retrieving<'Project'>;
    // const filter = status ? `[status=${status}]` : undefined;
    const filter = undefined;
    const { response, data } = await callGet('Project', query);
    const dataArray = Array.isArray(data) ? data : [data];
    if (!all) {
      const statusOptions = { concept, active, finished, archived };
      const hasSpecificFilters = Object.values(statusOptions).some(
        (v) => v !== undefined,
      );
      if (hasSpecificFilters) {
        // Include only statuses that are explicitly set to true
        const statusesToInclude = Object.entries(statusOptions)
          .filter(([_, value]) => value === true)
          .map(([key, _]) => key);

        const filtered = dataArray.filter((p) =>
          statusesToInclude.includes(p.status),
        );
        return { ...response, data: filtered };
      } else {
        // Default: exclude concept and archived
        const filtered = dataArray.filter(
          (p) => !['concept', 'archived'].includes(p.status),
        );
        return { ...response, data: filtered };
      }
    }
    return response;
  } catch (error: any | { response?: any }) {
    throw error?.response || error;
  }
};

export const postProjectData = async (data: IProject) => {
  try {
    const response: any = await api.post('Project', prepareForCreate(data));
    return response;
  } catch (error: any | { response?: any }) {
    throw error?.response || error;
  }
};

export const patchProjectData = async (data: IProject) => {
  try {
    const response = await api.patch(
      'Project/' + data.id,
      prepareForUpdate(data),
    );
    return response;
  } catch (error: any | { response?: any }) {
    throw error?.response || error;
  }
};

export const deleteProjectData = async (id: number) => {
  try {
    const response: any = await api.delete('Project/' + id);
    return response;
  } catch (error: any | { response?: any }) {
    throw error?.response || error;
  }
};
